import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import {
  Calendar,
  Clock,
  Users,
  CheckCircle,
  AlertCircle,
  Package,
  FileText,
  Upload,
  X,
  Home,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  Check,
  CreditCard
} from 'lucide-react';
import axios from 'axios';

// Payment Modal Component
const PaymentModal = ({ isOpen, onClose, amount, onSuccess, token, primaryColor }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError('');

    const cardElement = elements.getElement(CardElement);

    try {
      // Create payment method
      const { error: methodError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (methodError) {
        setError(methodError.message);
        setProcessing(false);
        return;
      }

      // Create payment intent on backend
      const response = await axios.post(
        `${API_BASE_URL}/api/create-payment-intent`,
        {
          amount: Math.round(amount * 100), // Convert to cents
          currency: 'usd',
          payment_method_id: paymentMethod.id
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log('Payment intent response:', response.data);

      // Check if backend returned an error
      if (!response.data.success) {
        setError(response.data.error || 'Payment processing failed');
        setProcessing(false);
        return;
      }

      const { client_secret } = response.data;

      // Confirm payment
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(client_secret);

      if (confirmError) {
        setError(confirmError.message);
        setProcessing(false);
      } else if (paymentIntent.status === 'succeeded') {
        onSuccess(paymentIntent.id);
        onClose();
      }
    } catch (err) {
      console.error('Payment error:', err);
      const errorMessage = err.response?.data?.detail || err.response?.data?.error || err.message || 'Payment failed. Please try again.';
      setError(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="px-6 py-4 border-b border-gray-200" style={{ backgroundColor: primaryColor }}>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Payment Required
            </h3>
            <button
              onClick={onClose}
              className="p-1 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-white" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-4">
            <p className="text-gray-700 mb-2">
              A token payment of <span className="font-semibold">${amount.toFixed(2)}</span> is required to secure your reservation.
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Card Details
            </label>
            <div className="border border-gray-300 rounded-lg p-3">
              <CardElement
                options={{
                  style: {
                    base: {
                      fontSize: '16px',
                      color: '#424770',
                      '::placeholder': {
                        color: '#aab7c4',
                      },
                    },
                  },
                }}
              />
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!stripe || processing}
              className="flex-1 px-4 py-2 text-white rounded-lg disabled:opacity-50"
              style={{ backgroundColor: primaryColor }}
            >
              {processing ? 'Processing...' : `Pay $${amount.toFixed(2)}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const BookBoarding = () => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Initial modal
  const [showTypeSelector, setShowTypeSelector] = useState(true);
  const [reservationType, setReservationType] = useState(null); // 'tour' or 'boarding'

  // Current step
  const [currentStep, setCurrentStep] = useState(0);

  // Settings
  const [settings, setSettings] = useState(null);

  // Date and Time Selection for Tours
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [businessTime, setBusinessTime] = useState(null);

  // Date and Time Selection for Boarding
  const [checkInDate, setCheckInDate] = useState(null);
  const [checkInTime, setCheckInTime] = useState(null);
  const [checkOutDate, setCheckOutDate] = useState(null);
  const [checkOutTime, setCheckOutTime] = useState(null);

  // Service Selection
  const [availableServices, setAvailableServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [showServiceModal, setShowServiceModal] = useState(false);

  // Form Data
  const [formFields, setFormFields] = useState([]);
  const [tourFields, setTourFields] = useState([]);
  const [roomFields, setRoomFields] = useState([]);
  const [specialInstructionFields, setSpecialInstructionFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [roomData, setRoomData] = useState({});
  const [specialInstructionsData, setSpecialInstructionsData] = useState({});

  // Documents
  const [uploadedDocuments, setUploadedDocuments] = useState([]);

  // Payment
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);

  // Confirmation
  const [confirmedReservation, setConfirmedReservation] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
  const primaryColor = 'var(--theme-primary)';

  // Initialize Stripe
  const [stripePromise, setStripePromise] = useState(null);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/boarding-settings`);
      if (response.data.success) {
        setSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  }, [API_BASE_URL]);

  const fetchFormFields = useCallback(async () => {
    try {
      const formResponse = await axios.get(`${API_BASE_URL}/api/boarding-form-fields?section=form`);
      const tourResponse = await axios.get(`${API_BASE_URL}/api/boarding-form-fields?section=tour`);
      const roomResponse = await axios.get(`${API_BASE_URL}/api/boarding-form-fields?section=rooms`);
      const specialResponse = await axios.get(`${API_BASE_URL}/api/boarding-form-fields?section=special_instructions`);
      
      if (formResponse.data.success) setFormFields(formResponse.data.fields);
      if (tourResponse.data.success) setTourFields(tourResponse.data.fields);
      if (roomResponse.data.success) setRoomFields(roomResponse.data.fields);
      if (specialResponse.data.success) setSpecialInstructionFields(specialResponse.data.fields);
    } catch (error) {
      console.error('Error fetching form fields:', error);
    }
  }, [API_BASE_URL]);

  const fetchBusinessTime = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/business-time`);
      if (response.data.success) {
        setBusinessTime(response.data);
      }
    } catch (error) {
      console.error('Error fetching business time:', error);
      // Fallback to browser time
      const now = new Date();
      setBusinessTime({
        current_time: now.toISOString(),
        date: now.toISOString().split('T')[0],
        time: now.toTimeString().split(' ')[0],
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      });
    }
  }, [API_BASE_URL]);

  const initializeStripe = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/stripe/publishable-key`);
      if (response.data.success && response.data.publishable_key) {
        const stripe = await loadStripe(response.data.publishable_key);
        setStripePromise(Promise.resolve(stripe));
      }
    } catch (error) {
      console.error('Error fetching Stripe key:', error);
      // Fallback to environment variable
      const fallbackKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
      if (fallbackKey) {
        const stripe = await loadStripe(fallbackKey);
        setStripePromise(Promise.resolve(stripe));
      }
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    fetchSettings();
    fetchFormFields();
    fetchBusinessTime();
    initializeStripe();
  }, [fetchSettings, fetchFormFields, fetchBusinessTime, initializeStripe]);

  const checkAvailability = async () => {
    if (!checkInDate || !checkOutDate || !checkInTime || !checkOutTime) {
      setMessage({ type: 'error', text: 'Please select both dates and times' });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/boarding/check-availability`,
        {
          check_in_date: checkInDate,
          check_out_date: checkOutDate
        }
      );

      console.log('Availability check response:', response.data);
      setAvailableServices(response.data.available_services || []);
      
      if (!response.data.available_services || response.data.available_services.length === 0) {
        setMessage({ type: 'error', text: 'No boarding spaces available for the selected dates. Please try different dates.' });
      } else {
        // Move to next step: service selection if enabled, otherwise form details (step 1 or 2)
        // Step 0: Select Dates & Times
        // Step 1: Select Service (if enabled)
        // Step 2 (or 1 if services disabled): Your Details
        const nextStep = settings?.services_enabled ? 1 : 2;
        console.log('Moving to next step:', nextStep);
        setCurrentStep(nextStep);
        setMessage({ type: '', text: '' }); // Clear any previous error messages
      }
    } catch (error) {
      console.error('Error checking availability:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to check availability. Please try again.';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  const generateTimeSlots = (startTime = '07:00', endTime = '19:00') => {
    const slots = [];
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    
    let currentTime = startHour * 60 + startMin;
    const endTimeMinutes = endHour * 60 + endMin;
    
    // Use 30-minute intervals for drop-off and pickup times
    while (currentTime < endTimeMinutes) {
      const hour = Math.floor(currentTime / 60);
      const min = currentTime % 60;
      const timeStr = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
      slots.push(timeStr);
      currentTime += 30;  // 30-minute intervals
    }
    
    return slots;
  };

  const generateMonths = () => {
    const months = [];
    const today = new Date();
    
    for (let i = 0; i < 6; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() + i, 1);
      months.push({
        value: date,
        label: date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      });
    }
    
    return months;
  };

  const getDatesInMonth = (monthDate) => {
    const dates = [];
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      if (date >= today) {
        dates.push(date);
      }
    }
    
    return dates;
  };

  // Calculate duration in hours between check-in and check-out
  const calculateStayDuration = () => {
    if (!checkInDate || !checkInTime || !checkOutDate || !checkOutTime) {
      return 0;
    }
    
    const checkIn = new Date(`${checkInDate}T${checkInTime}`);
    const checkOut = new Date(`${checkOutDate}T${checkOutTime}`);
    
    const durationMs = checkOut - checkIn;
    const durationHours = durationMs / (1000 * 60 * 60);
    
    return Math.max(0, durationHours); // Ensure non-negative
  };

  // Calculate price based on duration and variants using greedy algorithm
  const calculateVariantPrice = (service) => {
    if (!service.is_variable || !service.variants || service.variants.length === 0) {
      // For non-variable services, return base price
      return service.price || 0;
    }

    const durationHours = calculateStayDuration();
    if (durationHours <= 0) {
      return 0;
    }

    // Parse variants and convert to hours
    const variants = service.variants
      .filter(v => v.is_enabled)
      .map(v => {
        const variantName = v.variant_name.toLowerCase();
        let hours = 0;
        
        if (variantName.includes('hr')) {
          hours = parseFloat(variantName.replace('hr', ''));
        } else if (variantName.includes('min')) {
          hours = parseFloat(variantName.replace('min', '')) / 60;
        }
        
        return {
          hours,
          price: v.price,
          name: v.variant_name
        };
      })
      .sort((a, b) => b.hours - a.hours); // Sort descending by hours

    // Greedy algorithm: use largest variants first
    let remainingHours = durationHours;
    let totalPrice = 0;
    const breakdown = [];

    for (const variant of variants) {
      if (variant.hours > 0) {
        const count = Math.floor(remainingHours / variant.hours);
        if (count > 0) {
          totalPrice += count * variant.price;
          remainingHours -= count * variant.hours;
          breakdown.push({ variant: variant.name, count, price: variant.price });
        }
      }
    }

    // If there are remaining hours, use the smallest variant to cover them
    if (remainingHours > 0 && variants.length > 0) {
      const smallestVariant = variants[variants.length - 1];
      const additionalCount = Math.ceil(remainingHours / smallestVariant.hours);
      totalPrice += additionalCount * smallestVariant.price;
      
      // Add to existing breakdown or create new entry
      const existingEntry = breakdown.find(b => b.variant === smallestVariant.name);
      if (existingEntry) {
        existingEntry.count += additionalCount;
      } else {
        breakdown.push({ variant: smallestVariant.name, count: additionalCount, price: smallestVariant.price });
      }
    }

    return totalPrice;
  };

  const handleTypeSelect = (type) => {
    setReservationType(type);
    setShowTypeSelector(false);
    setCurrentStep(0);
  };

  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep(currentStep + 1);
      // Scroll to top smoothly
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
    // Scroll to top smoothly
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const validateStep = () => {
    // Add validation logic for each step
    return true;
  };

  const handleBooking = async (skipPaymentCheck = false) => {
    // Determine which payment setting to use based on reservation type
    const isPaymentEnabled = reservationType === 'tour' 
      ? settings?.tour_payment_enabled 
      : settings?.boarding_payment_enabled;

    // Debug: Log payment settings
    console.log('Payment Check:', {
      skipPaymentCheck,
      reservationType,
      tour_payment_enabled: settings?.tour_payment_enabled,
      boarding_payment_enabled: settings?.boarding_payment_enabled,
      isPaymentEnabled,
      token_amount: settings?.token_amount,
      token_amount_type: typeof settings?.token_amount,
      token_amount_number: Number(settings?.token_amount),
      paymentCompleted,
      settingsObject: settings,
      shouldShowPayment: !skipPaymentCheck && isPaymentEnabled && Number(settings?.token_amount || 0) > 0 && !paymentCompleted
    });

    // Check if payment is required and not completed
    // Convert token_amount to number to ensure proper comparison
    const tokenAmount = Number(settings?.token_amount || 0);
    if (!skipPaymentCheck && isPaymentEnabled && tokenAmount > 0 && !paymentCompleted) {
      console.log('🔔 Opening payment modal');
      setShowPaymentModal(true);
      return;
    } else {
      console.log('⚠️ Skipping payment modal:', {
        reason: skipPaymentCheck ? 'skipPaymentCheck is true' : 
                !isPaymentEnabled ? 'payment not enabled for this reservation type' :
                tokenAmount <= 0 ? 'token amount is 0 or invalid' :
                paymentCompleted ? 'payment already completed' : 'unknown'
      });
    }

    setLoading(true);
    
    try {
      const reservationData = {
        reservation_type: reservationType,
        dropoff_date: reservationType === 'boarding' ? checkInDate : selectedDate?.toISOString().split('T')[0],
        dropoff_time: reservationType === 'boarding' ? checkInTime : selectedTimeSlot,
        pickup_date: reservationType === 'boarding' ? checkOutDate : null,
        pickup_time: reservationType === 'boarding' ? checkOutTime : null,
        form_details: formData,
        room_details: roomData,
        special_instructions: specialInstructionsData,
        selected_service_id: selectedService?.id,
        selected_service: selectedService,
        assigned_space_id: selectedService?.assigned_space_id,
        assigned_space_name: selectedService?.assigned_space_name,
        uploaded_documents: uploadedDocuments,
        payment_token_amount: isPaymentEnabled ? settings.token_amount : null,
        payment_transaction_id: skipPaymentCheck || paymentCompleted ? 'stripe_payment_' + Date.now() : null
      };

      const response = await axios.post(
        `${API_BASE_URL}/api/boarding-reservations`,
        reservationData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setConfirmedReservation(response.data.reservation);
        setShowConfirmationModal(true);
      }
    } catch (error) {
      showMessage('error', 'Failed to create reservation');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const getTourSteps = () => [
    { id: 0, title: 'Select Date & Time', icon: Calendar },
    { id: 1, title: 'Your Details', icon: Users },
    { id: 2, title: 'Review', icon: FileText }
  ];

  const getBoardingSteps = () => {
    const steps = [
      { id: 0, title: 'Select Dates & Times', icon: Calendar }
    ];

    if (settings?.services_enabled) {
      steps.push({ id: steps.length, title: 'Select Service', icon: Package });
    }

    steps.push({ id: steps.length, title: 'Your Details', icon: Users });

    if (settings?.document_upload_enabled) {
      steps.push({ id: steps.length, title: 'Upload Documents', icon: Upload });
    }

    steps.push({ id: steps.length, title: 'Review', icon: FileText });

    return steps;
  };

  const steps = reservationType === 'tour' ? getTourSteps() : getBoardingSteps();

  const renderFormField = (field, data, setData) => {
    const fieldKey = field.id;
    const value = data[fieldKey] || '';

    switch (field.field_type) {
      case 'text':
      case 'email':
      case 'phone':
        return (
          <input
            type={field.field_type}
            value={value}
            onChange={(e) => setData({ ...data, [fieldKey]: e.target.value })}
            placeholder={field.placeholder}
            required={field.required}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent"
          />
        );

      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => setData({ ...data, [fieldKey]: e.target.value })}
            required={field.required}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent"
          />
        );

      case 'time':
        return (
          <div className="grid grid-cols-2 gap-2">
            <select
              value={value.split(':')[0] || ''}
              onChange={(e) => {
                const newValue = `${e.target.value}:${value.split(':')[1] || '00'}`;
                setData({ ...data, [fieldKey]: newValue });
              }}
              required={field.required}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)]"
            >
              <option value="">Hour</option>
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={i.toString().padStart(2, '0')}>
                  {i.toString().padStart(2, '0')}
                </option>
              ))}
            </select>
            <select
              value={value.split(':')[1] || ''}
              onChange={(e) => {
                const newValue = `${value.split(':')[0] || '00'}:${e.target.value}`;
                setData({ ...data, [fieldKey]: newValue });
              }}
              required={field.required}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)]"
            >
              <option value="">Min</option>
              <option value="00">00</option>
              <option value="15">15</option>
              <option value="30">30</option>
              <option value="45">45</option>
            </select>
          </div>
        );

      case 'dropdown':
        return (
          <select
            value={value}
            onChange={(e) => setData({ ...data, [fieldKey]: e.target.value })}
            required={field.required}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)]"
          >
            <option value="">Select...</option>
            {field.options.map((opt, idx) => (
              <option key={idx} value={opt}>{opt}</option>
            ))}
          </select>
        );

      case 'radio':
        return (
          <div className="space-y-2">
            {field.options.map((opt, idx) => (
              <label key={idx} className="flex items-center gap-2">
                <input
                  type="radio"
                  name={fieldKey}
                  value={opt}
                  checked={value === opt}
                  onChange={(e) => setData({ ...data, [fieldKey]: e.target.value })}
                  required={field.required}
                  className="text-[rgb(41,173,211)] focus:ring-[rgb(41,173,211)]"
                />
                <span className="text-sm text-gray-700">{opt}</span>
              </label>
            ))}
          </div>
        );

      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => setData({ ...data, [fieldKey]: e.target.value })}
            placeholder={field.placeholder}
            required={field.required}
            rows="3"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent"
          />
        );

      case 'description':
        return (
          <p className="text-sm text-gray-600">{field.description}</p>
        );

      default:
        return null;
    }
  };

  if (!settings) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[rgb(41,173,211)] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <div className="min-h-screen bg-gray-50 pb-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Type Selector Modal - Compact Version */}
        {showTypeSelector && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">What would you like to do?</h2>
              
              <div className="space-y-3">
                {/* Schedule Tour */}
                <button
                  onClick={() => handleTypeSelect('tour')}
                  className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-[rgb(41,173,211)] hover:bg-blue-50 transition-all group flex items-center gap-4"
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center group-hover:bg-[rgb(41,173,211)] transition-colors flex-shrink-0">
                    <Home className="h-6 w-6 text-[rgb(41,173,211)] group-hover:text-white" />
                  </div>
                  <div className="text-left flex-1">
                    <h3 className="font-semibold text-gray-900">Schedule A Tour</h3>
                    <p className="text-xs text-gray-600">Visit our facility</p>
                  </div>
                </button>

                {/* Boarding Reservation */}
                <button
                  onClick={() => handleTypeSelect('boarding')}
                  className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-[rgb(41,173,211)] hover:bg-blue-50 transition-all group flex items-center gap-4"
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center group-hover:bg-[rgb(41,173,211)] transition-colors flex-shrink-0">
                    <Calendar className="h-6 w-6 text-[rgb(41,173,211)] group-hover:text-white" />
                  </div>
                  <div className="text-left flex-1">
                    <h3 className="font-semibold text-gray-900">Make Boarding Reservation</h3>
                    <p className="text-xs text-gray-600">Book a stay for your pet</p>
                  </div>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main Booking Flow */}
        {!showTypeSelector && (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {reservationType === 'tour' ? 'Schedule Tour' : 'Book Boarding'}
              </h2>
            </div>

            {/* Message Display */}
            {message.text && (
              <div className={`px-6 py-3 ${
                message.type === 'error' ? 'bg-red-50 border-b border-red-200' : 
                message.type === 'success' ? 'bg-green-50 border-b border-green-200' : 
                'bg-blue-50 border-b border-blue-200'
              }`}>
                <p className={`text-sm ${
                  message.type === 'error' ? 'text-red-600' : 
                  message.type === 'success' ? 'text-green-600' : 
                  'text-theme-blue'
                }`}>
                  {message.text}
                </p>
              </div>
            )}

            {/* Tabs - BookAppointment Style */}
            <div className="bg-white rounded-lg shadow-sm border-b border-gray-200">
              <nav className="-mb-px flex px-6" aria-label="Tabs">
                {steps.map((step) => {
                  const Icon = step.icon;
                  const isActive = currentStep === step.id;
                  const isCompleted = currentStep > step.id;

                  return (
                    <button
                      key={step.id}
                      className={`flex-1 py-4 px-1 border-b-2 font-medium text-sm flex items-center justify-center space-x-2 ${
                        isActive
                          ? 'border-theme-blue text-theme-blue'
                          : isCompleted
                          ? 'border-theme-blue text-theme-blue'
                          : 'border-transparent text-gray-500'
                      }`}
                    >
                      {isCompleted ? (
                        <Check className="h-4 w-4" />
                      ) : (
                        <Icon className="h-4 w-4" />
                      )}
                      <span>{step.title}</span>
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Step Content */}
            <div className="p-6">
              {/* Tour Flow - Step 0: Select Time */}
              {reservationType === 'tour' && currentStep === 0 && (
                <div>
                  <div className="mb-6 max-w-sm">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Select Date</label>
                    <select
                      value={selectedDate ? selectedDate.toISOString().split('T')[0] : ''}
                      onChange={(e) => setSelectedDate(e.target.value ? new Date(e.target.value) : null)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent"
                    >
                      <option value="">Select a date...</option>
                      {(() => {
                        const dates = [];
                        // Use business time if available, otherwise fallback to browser time
                        const today = businessTime ? new Date(businessTime.current_time) : new Date();
                        const currentDate = businessTime ? businessTime.date : new Date().toISOString().split('T')[0];
                        const currentTime = businessTime ? businessTime.time : new Date().toTimeString().split(' ')[0];
                        
                        // Check if today has any available time slots
                        const allSlots = generateTimeSlots(settings?.start_time || '09:00', settings?.end_time || '17:00');
                        const [currentHour, currentMinute] = currentTime.split(':').map(Number);
                        const currentTimeInMinutes = currentHour * 60 + currentMinute;
                        
                        // Find the last time slot
                        let latestSlotInMinutes = 0;
                        if (allSlots.length > 0) {
                          const lastSlot = allSlots[allSlots.length - 1];
                          const [lastHour, lastMinute] = lastSlot.split(':').map(Number);
                          latestSlotInMinutes = lastHour * 60 + lastMinute;
                        }
                        
                        // If current time is past the last slot, start from tomorrow
                        const startDay = currentTimeInMinutes >= latestSlotInMinutes ? 1 : 0;
                        
                        for (let i = startDay; i < 7 + startDay; i++) {
                          const date = new Date(today);
                          date.setDate(today.getDate() + i);
                          const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
                          const monthDay = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                          dates.push(
                            <option key={i} value={date.toISOString().split('T')[0]}>
                              {dayName} ({monthDay})
                            </option>
                          );
                        }
                        return dates;
                      })()}
                    </select>
                  </div>

                  {selectedDate && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Select Time Slot</label>
                      <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                        {(() => {
                          const allSlots = generateTimeSlots(settings?.start_time || '09:00', settings?.end_time || '17:00');
                          
                          // Use business time if available, otherwise fallback to browser time
                          const currentDate = businessTime ? businessTime.date : new Date().toISOString().split('T')[0];
                          const currentTime = businessTime ? businessTime.time : new Date().toTimeString().split(' ')[0];
                          
                          // Check if selected date is today (in business timezone)
                          const selectedDateStr = typeof selectedDate === 'string' ? selectedDate : selectedDate.toISOString().split('T')[0];
                          const isToday = selectedDateStr === currentDate;
                          
                          if (isToday) {
                            // Filter out past time slots for today using business time
                            const [currentHour, currentMinute] = currentTime.split(':').map(Number);
                            const currentTimeInMinutes = currentHour * 60 + currentMinute;
                            
                            return allSlots.filter(slot => {
                              const [slotHour, slotMinute] = slot.split(':').map(Number);
                              const slotTimeInMinutes = slotHour * 60 + slotMinute;
                              return slotTimeInMinutes > currentTimeInMinutes;
                            }).map(slot => (
                              <button
                                key={slot}
                                onClick={() => setSelectedTimeSlot(slot)}
                                className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                                  selectedTimeSlot === slot
                                    ? 'bg-[rgb(41,173,211)] text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                              >
                                {slot}
                              </button>
                            ));
                          }
                          
                          // For future dates, show all slots
                          return allSlots.map(slot => (
                            <button
                              key={slot}
                              onClick={() => setSelectedTimeSlot(slot)}
                              className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                                selectedTimeSlot === slot
                                  ? 'bg-[rgb(41,173,211)] text-white'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              {slot}
                            </button>
                          ));
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Boarding Flow - Step 0: Drop-off/Pickup Date & Time Selection */}
              {reservationType === 'boarding' && currentStep === 0 && (
                <div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    {/* Drop-off Date & Time */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Drop-off Date</label>
                      <input
                        type="date"
                        value={checkInDate || ''}
                        onChange={(e) => setCheckInDate(e.target.value)}
                        min={businessTime ? businessTime.date : new Date().toISOString().split('T')[0]}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--theme-primary)] focus:border-[var(--theme-primary)]"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Drop-off Time</label>
                      <select
                        value={checkInTime || ''}
                        onChange={(e) => setCheckInTime(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--theme-primary)] focus:border-[var(--theme-primary)]"
                      >
                        <option value="">Select Time</option>
                        {(() => {
                          const allSlots = generateTimeSlots(settings.dropoff_start_time || '07:00', settings.dropoff_end_time || '19:00');
                          
                          // Use business time to filter past slots if drop-off date is today
                          if (checkInDate && businessTime) {
                            const currentDate = businessTime.date;
                            const currentTime = businessTime.time;
                            const isToday = checkInDate === currentDate;
                            
                            if (isToday) {
                              // Filter out past time slots
                              const [currentHour, currentMinute] = currentTime.split(':').map(Number);
                              const currentTimeInMinutes = currentHour * 60 + currentMinute;
                              
                              return allSlots.filter(slot => {
                                const [slotHour, slotMinute] = slot.split(':').map(Number);
                                const slotTimeInMinutes = slotHour * 60 + slotMinute;
                                return slotTimeInMinutes > currentTimeInMinutes;
                              }).map(slot => (
                                <option key={slot} value={slot}>{slot}</option>
                              ));
                            }
                          }
                          
                          // For future dates or when business time not loaded, show all slots
                          return allSlots.map(slot => (
                            <option key={slot} value={slot}>{slot}</option>
                          ));
                        })()}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    {/* Pickup Date & Time */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Pickup Date</label>
                      <input
                        type="date"
                        value={checkOutDate || ''}
                        onChange={(e) => setCheckOutDate(e.target.value)}
                        min={checkInDate || (businessTime ? businessTime.date : new Date().toISOString().split('T')[0])}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--theme-primary)] focus:border-[var(--theme-primary)]"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Pickup Time</label>
                      <select
                        value={checkOutTime || ''}
                        onChange={(e) => setCheckOutTime(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--theme-primary)] focus:border-[var(--theme-primary)]"
                      >
                        <option value="">Select Time</option>
                        {(() => {
                          const allSlots = generateTimeSlots(settings.pickup_start_time || '07:00', settings.pickup_end_time || '19:00');
                          
                          // Use business time to filter past slots if pickup date is today
                          if (checkOutDate && businessTime) {
                            const currentDate = businessTime.date;
                            const currentTime = businessTime.time;
                            const isToday = checkOutDate === currentDate;
                            
                            if (isToday) {
                              // Filter out past time slots
                              const [currentHour, currentMinute] = currentTime.split(':').map(Number);
                              const currentTimeInMinutes = currentHour * 60 + currentMinute;
                              
                              return allSlots.filter(slot => {
                                const [slotHour, slotMinute] = slot.split(':').map(Number);
                                const slotTimeInMinutes = slotHour * 60 + slotMinute;
                                return slotTimeInMinutes > currentTimeInMinutes;
                              }).map(slot => (
                                <option key={slot} value={slot}>{slot}</option>
                              ));
                            }
                          }
                          
                          // For future dates or when business time not loaded, show all slots
                          return allSlots.map(slot => (
                            <option key={slot} value={slot}>{slot}</option>
                          ));
                        })()}
                      </select>
                    </div>
                  </div>

                  <button
                    onClick={checkAvailability}
                    disabled={!checkInDate || !checkOutDate || !checkInTime || !checkOutTime || loading}
                    className="w-full bg-[var(--theme-primary)] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#239bc1] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading ? 'Checking Availability...' : 'Check Availability'}
                  </button>
                </div>
              )}

              {/* Service Selection (Boarding only, if enabled) */}
              {reservationType === 'boarding' && settings?.services_enabled && 
               currentStep === (steps.findIndex(s => s.title === 'Select Service')) && (
                <div>
                  {availableServices.length === 0 ? (
                    <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                      <Package className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-600">No services available for the selected dates</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {availableServices.map(service => {
                        const isAvailable = service.is_available !== false;
                        return (
                        <div
                          key={service.id}
                          className={`rounded-lg border-2 overflow-hidden transition-all flex relative ${
                            !isAvailable
                              ? 'border-gray-200 bg-gray-50 opacity-75'
                              : selectedService?.id === service.id
                              ? 'border-theme-blue bg-blue-50 shadow-md'
                              : 'border-gray-200 hover:border-blue-300 hover:shadow'
                          }`}
                        >
                          {/* Service Image - Left Side */}
                          <div className="w-40 h-40 flex-shrink-0 bg-gray-50 relative">
                            {service.image_url ? (
                              <img 
                                src={service.image_url} 
                                alt={service.service_name}
                                className={`w-full h-full object-contain ${!isAvailable ? 'grayscale' : ''}`}
                              />
                            ) : (
                              <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                                <Package className="h-12 w-12 text-gray-400" />
                              </div>
                            )}
                            {/* Not Available Overlay */}
                            {!isAvailable && (
                              <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                                <div className="bg-red-500 text-white px-3 py-1.5 rounded-md text-xs font-bold">
                                  NOT AVAILABLE
                                </div>
                              </div>
                            )}
                          </div>
                          
                          {/* Service Details - Right Side */}
                          <div className="flex-1 p-3 flex flex-col">
                            <h4 className={`font-semibold text-sm mb-1.5 ${!isAvailable ? 'text-gray-500' : 'text-gray-900'}`}>
                              {service.service_name || service.name}
                            </h4>
                            <div className="space-y-1.5 mb-2 flex-1">
                              {/* Description */}
                              {service.description && (
                                <p className="text-xs text-gray-500 line-clamp-2">{service.description}</p>
                              )}
                              
                              {/* Space Info */}
                              {service.assigned_space_name && (
                                <p className="text-xs text-gray-600 flex items-center gap-1">
                                  <Home className="h-3 w-3" />
                                  Space: {service.assigned_space_name}
                                </p>
                              )}
                              
                              {!isAvailable && (
                                <p className="text-xs text-red-600 font-medium">
                                  No spaces available for this period
                                </p>
                              )}
                              
                              {/* Price and Duration on same row */}
                              <div className="flex items-center justify-between">
                                <div>
                                  {/* Display price range for variable services, fixed price otherwise */}
                                  {service.is_variable && service.variants && service.variants.length > 0 ? (
                                    (() => {
                                      const enabledVariants = service.variants.filter(v => v.is_enabled);
                                      const prices = enabledVariants.map(v => v.price);
                                      const minPrice = Math.min(...prices);
                                      const maxPrice = Math.max(...prices);
                                      return (
                                        <p className={`text-sm font-semibold ${!isAvailable ? 'text-gray-500' : 'text-gray-900'}`}>
                                          ${minPrice.toFixed(2)} - ${maxPrice.toFixed(2)}
                                        </p>
                                      );
                                    })()
                                  ) : service.price ? (
                                    <p className={`text-sm font-semibold ${!isAvailable ? 'text-gray-500' : 'text-gray-900'}`}>
                                      ${service.price.toFixed(2)}
                                    </p>
                                  ) : null}
                                </div>
                                {/* Display duration range for variable services */}
                                {service.is_variable && service.variants && service.variants.length > 0 ? (
                                  (() => {
                                    const enabledVariants = service.variants.filter(v => v.is_enabled);
                                    const durations = enabledVariants.map(v => v.variant_name);
                                    const minDuration = durations[0];
                                    const maxDuration = durations[durations.length - 1];
                                    return (
                                      <p className="text-xs text-gray-600 flex items-center">
                                        <Clock className="h-3 w-3 mr-1" />
                                        {minDuration} - {maxDuration}
                                      </p>
                                    );
                                  })()
                                ) : service.duration_minutes && !service.is_variable ? (
                                  <p className="text-xs text-gray-600 flex items-center">
                                    <Clock className="h-3 w-3 mr-1" />
                                    {Math.floor(service.duration_minutes / 60)} hrs
                                  </p>
                                ) : null}
                              </div>
                            </div>
                            
                            {/* Action Buttons */}
                            <div className="flex gap-2 mt-auto">
                              <button
                                onClick={() => setShowServiceModal(service)}
                                className="flex-1 px-2 py-1.5 text-xs font-medium border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors"
                              >
                                See Details
                              </button>
                              <button
                                onClick={() => {
                                  if (isAvailable) {
                                    setSelectedService(service);
                                    setSelectedOptions({});
                                  }
                                }}
                                disabled={!isAvailable}
                                className={`flex-1 px-2 py-1.5 text-xs font-medium text-white rounded transition-colors ${
                                  !isAvailable ? 'bg-gray-400 cursor-not-allowed' : ''
                                }`}
                                style={isAvailable ? { backgroundColor: primaryColor } : {}}
                              >
                                {isAvailable ? 'Select' : 'Unavailable'}
                              </button>
                            </div>
                            
                            {/* Selected Indicator */}
                            {selectedService?.id === service.id && isAvailable && (
                              <div className="mt-1.5 flex items-center justify-center gap-1 text-theme-blue font-medium text-xs">
                                <CheckCircle className="h-3 w-3" />
                                <span>Selected</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )})}
                    </div>
                  )}
                </div>
              )}

              {/* Form Details */}
              {currentStep === steps.findIndex(s => s.title === 'Your Details') && (
                <div className="space-y-6">
                  {/* Form Fields */}
                  {(() => {
                    // Use tour fields for tour reservations, form fields for boarding
                    const currentFormFields = reservationType === 'tour' ? tourFields : formFields;
                    return currentFormFields.length > 0 && (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {currentFormFields.map(field => (
                          <div key={field.id} className={field.field_type === 'description' ? 'md:col-span-2' : ''}>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {field.field_name}
                              {field.required && <span className="text-red-600 ml-1">*</span>}
                            </label>
                            {field.description && field.field_type !== 'description' && (
                              <p className="text-xs text-gray-500 mb-2">{field.description}</p>
                            )}
                            {renderFormField(field, formData, setFormData)}
                          </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Room Fields */}
                  {roomFields.length > 0 && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {roomFields.map(field => (
                          <div key={field.id} className={field.field_type === 'description' ? 'md:col-span-2' : ''}>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {field.field_name}
                              {field.required && <span className="text-red-600 ml-1">*</span>}
                            </label>
                            {field.description && field.field_type !== 'description' && (
                              <p className="text-xs text-gray-500 mb-2">{field.description}</p>
                            )}
                            {renderFormField(field, roomData, setRoomData)}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Special Instructions - Hide for tour reservations */}
                  {reservationType !== 'tour' && specialInstructionFields.length > 0 && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {specialInstructionFields.map(field => (
                          <div key={field.id} className={field.field_type === 'description' ? 'md:col-span-2' : ''}>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {field.field_name}
                              {field.required && <span className="text-red-600 ml-1">*</span>}
                            </label>
                            {field.description && field.field_type !== 'description' && (
                              <p className="text-xs text-gray-500 mb-2">{field.description}</p>
                            )}
                            {renderFormField(field, specialInstructionsData, setSpecialInstructionsData)}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Upload Documents */}
              {settings.document_upload_enabled && 
               currentStep === steps.findIndex(s => s.title === 'Upload Documents') && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Upload Documents</h2>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                    <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-2">Upload PDF documents</p>
                    <input
                      type="file"
                      accept=".pdf"
                      multiple
                      className="hidden"
                      id="file-upload"
                    />
                    <label
                      htmlFor="file-upload"
                      className="inline-block px-4 py-2 bg-[rgb(41,173,211)] text-white rounded-lg cursor-pointer hover:opacity-90"
                    >
                      Choose Files
                    </label>
                  </div>
                </div>
              )}

              {/* Review */}
              {currentStep === steps.findIndex(s => s.title === 'Review') && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-5xl">
                  {reservationType === 'boarding' ? (
                    <>
                      {checkInDate && (
                        <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border-2 border-blue-200 shadow-sm">
                          <div className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2 flex items-center gap-1">
                            <Calendar className="h-3.5 w-3.5" />
                            Drop-off
                          </div>
                          <div className="text-base font-semibold text-gray-900">
                            {new Date(checkInDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                          </div>
                          {checkInTime && (
                            <div className="text-sm text-gray-700 mt-1 flex items-center gap-1">
                              <Clock className="h-3.5 w-3.5" />
                              {checkInTime}
                            </div>
                          )}
                        </div>
                      )}

                      {checkOutDate && (
                        <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border-2 border-green-200 shadow-sm">
                          <div className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-2 flex items-center gap-1">
                            <Calendar className="h-3.5 w-3.5" />
                            Pick-up
                          </div>
                          <div className="text-base font-semibold text-gray-900">
                            {new Date(checkOutDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                          </div>
                          {checkOutTime && (
                            <div className="text-sm text-gray-700 mt-1 flex items-center gap-1">
                              <Clock className="h-3.5 w-3.5" />
                              {checkOutTime}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border-2 border-blue-200 shadow-sm">
                        <div className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2 flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          Date
                        </div>
                        <div className="text-base font-semibold text-gray-900">
                          {selectedDate?.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                        </div>
                      </div>

                      <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border-2 border-green-200 shadow-sm">
                        <div className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-2 flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5" />
                          Time
                        </div>
                        <div className="text-base font-semibold text-gray-900">{selectedTimeSlot}</div>
                      </div>
                    </>
                  )}

                  {selectedService && (
                    <div className="p-4 bg-blue-50 rounded-lg border-2 border-blue-200 md:col-span-2">
                      <div className="text-sm font-semibold text-blue-900 mb-3 flex items-center gap-2">
                        <Package className="h-4 w-4" />
                        Service Details
                      </div>
                      <div className="space-y-3">
                        {/* Service Name */}
                        <div>
                          <div className="text-base text-gray-900 font-bold">
                            {selectedService.service_name || selectedService.name}
                          </div>
                          {selectedService.assigned_space_name && (
                            <div className="text-sm text-blue-700 mt-1 flex items-center gap-1 font-medium">
                              <Home className="h-4 w-4" />
                              Assigned Space: {selectedService.assigned_space_name}
                            </div>
                          )}
                        </div>
                        
                        {/* Service Description */}
                        {selectedService.description && (
                          <div className="text-sm text-gray-700 bg-white p-3 rounded border border-blue-100">
                            {selectedService.description}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Pricing Summary */}
                  <div className="md:col-span-2">
                  {selectedService && (
                    <div className="space-y-2">
                      {/* Duration Display */}
                      {checkInDate && checkInTime && checkOutDate && checkOutTime && (
                        <div className="flex items-center justify-between text-sm text-gray-600 px-3 py-2 bg-gray-50 rounded">
                          <span>Stay Duration</span>
                          <span className="font-semibold">
                            {(() => {
                              const duration = calculateStayDuration();
                              const days = Math.floor(duration / 24);
                              const hours = Math.floor(duration % 24);
                              if (days > 0) {
                                return `${days} day${days > 1 ? 's' : ''} ${hours} hr${hours !== 1 ? 's' : ''}`;
                              }
                              return `${duration.toFixed(1)} hours`;
                            })()}
                          </span>
                        </div>
                      )}
                      
                      {/* Base Service Price */}
                      {selectedService && (
                        <div className="bg-blue-50 rounded px-3 py-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-700">
                              {selectedService.is_variable ? 'Boarding Service (Duration-based)' : 'Boarding Service'}
                            </span>
                            <span className="font-semibold text-gray-900">
                              ${calculateVariantPrice(selectedService).toFixed(2)}
                            </span>
                          </div>
                          
                          {/* Variant Breakdown for variable services */}
                          {selectedService.is_variable && selectedService.variants && selectedService.variants.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-blue-200 space-y-1">
                              {(() => {
                                const durationHours = calculateStayDuration();
                                const variants = selectedService.variants
                                  .filter(v => v.is_enabled)
                                  .map(v => {
                                    const variantName = v.variant_name.toLowerCase();
                                    let hours = 0;
                                    if (variantName.includes('hr')) {
                                      hours = parseFloat(variantName.replace('hr', ''));
                                    } else if (variantName.includes('min')) {
                                      hours = parseFloat(variantName.replace('min', '')) / 60;
                                    }
                                    return { hours, price: v.price, name: v.variant_name };
                                  })
                                  .sort((a, b) => b.hours - a.hours);

                                let remainingHours = durationHours;
                                const breakdown = [];

                                // Calculate breakdown
                                for (const variant of variants) {
                                  if (variant.hours > 0) {
                                    const count = Math.floor(remainingHours / variant.hours);
                                    if (count > 0) {
                                      remainingHours -= count * variant.hours;
                                      breakdown.push({ ...variant, count });
                                    }
                                  }
                                }

                                // Handle remaining hours
                                if (remainingHours > 0 && variants.length > 0) {
                                  const smallestVariant = variants[variants.length - 1];
                                  const additionalCount = Math.ceil(remainingHours / smallestVariant.hours);
                                  const existingEntry = breakdown.find(b => b.name === smallestVariant.name);
                                  if (existingEntry) {
                                    existingEntry.count += additionalCount;
                                  } else {
                                    breakdown.push({ ...smallestVariant, count: additionalCount });
                                  }
                                }

                                return breakdown.map((item, idx) => (
                                  <div key={idx} className="flex items-center justify-between text-xs text-gray-600">
                                    <span className="ml-3">→ {item.count}× {item.name} @ ${item.price.toFixed(2)}</span>
                                    <span className="font-medium">${(item.count * item.price).toFixed(2)}</span>
                                  </div>
                                ));
                              })()}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Add-on Options */}
                      {selectedOptions && Object.keys(selectedOptions).length > 0 && (
                        <>
                          {Object.entries(selectedOptions).map(([optionId, value]) => {
                            if (!value) return null;
                            const option = selectedService.options?.find(opt => opt.option_id === optionId);
                            if (!option) return null;
                            
                            let optionPrice = 0;
                            let optionLabel = option.option_name;
                            
                            if ((option.option_type === 'single' || option.option_type === 'checkbox') && value === true && option.price) {
                              optionPrice = parseFloat(option.price);
                            }
                            
                            if (option.option_type === 'dropdown' && value) {
                              const choice = option.choices?.find(c => c.choice_id === value.choice_id);
                              if (choice?.price) {
                                optionPrice = parseFloat(choice.price);
                                optionLabel = `${option.option_name}: ${choice.choice_name}`;
                              }
                            }
                            
                            if (optionPrice <= 0) return null;
                            
                            return (
                              <div key={optionId} className="flex items-center justify-between text-sm text-gray-600 px-3 py-1">
                                <span>+ {optionLabel}</span>
                                <span className="font-semibold">${optionPrice.toFixed(2)}</span>
                              </div>
                            );
                          })}
                        </>
                      )}
                      
                      {/* Total */}
                      <div className="flex items-center justify-between bg-gradient-to-r from-green-50 to-blue-50 p-3 rounded border-2 border-green-200">
                        <span className="text-sm font-bold text-gray-900">Total Cost</span>
                        <div className="text-lg font-bold text-green-700">
                          ${(() => {
                            let total = calculateVariantPrice(selectedService);
                            
                            // Add option prices
                            if (selectedOptions) {
                              Object.entries(selectedOptions).forEach(([optionId, value]) => {
                                if (!value) return;
                                const option = selectedService.options?.find(opt => opt.option_id === optionId);
                                if (!option) return;
                                
                                if ((option.option_type === 'single' || option.option_type === 'checkbox') && value === true && option.price) {
                                  total += parseFloat(option.price);
                                }
                                
                                if (option.option_type === 'dropdown' && value) {
                                  const choice = option.choices?.find(c => c.choice_id === value.choice_id);
                                  if (choice?.price) {
                                    total += parseFloat(choice.price);
                                  }
                                }
                              });
                            }
                            
                            return total.toFixed(2);
                          })()}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                  {/* Form Details */}
                  {formData && Object.keys(formData).length > 0 && (
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 md:col-span-2">
                      <div className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        Your Details
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {(() => {
                          const currentFormFields = reservationType === 'tour' ? tourFields : formFields;
                          return currentFormFields.map(field => {
                            const value = formData[field.id];
                            if (!value) return null;
                            
                            return (
                              <div key={field.id} className={field.field_type === 'description' ? 'md:col-span-2' : ''}>
                                <div className="text-xs font-medium text-gray-600 mb-1">{field.field_name}</div>
                                <div className="text-sm text-gray-900">
                                  {field.field_type === 'checkbox' ? (value ? 'Yes' : 'No') : value}
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Room Details */}
                  {roomData && Object.keys(roomData).length > 0 && (
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 md:col-span-2">
                      <div className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <Home className="h-4 w-4" />
                        Room Details
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {roomFields.map(field => {
                          const value = roomData[field.id];
                          if (!value) return null;
                          
                          return (
                            <div key={field.id} className={field.field_type === 'description' ? 'md:col-span-2' : ''}>
                              <div className="text-xs font-medium text-gray-600 mb-1">{field.field_name}</div>
                              <div className="text-sm text-gray-900">
                                {field.field_type === 'checkbox' ? (value ? 'Yes' : 'No') : value}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Special Instructions */}
                  {specialInstructionsData && Object.keys(specialInstructionsData).length > 0 && (
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 md:col-span-2">
                      <div className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        Special Instructions
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {specialInstructionFields.map(field => {
                          const value = specialInstructionsData[field.id];
                          if (!value) return null;
                          
                          return (
                            <div key={field.id} className={field.field_type === 'description' ? 'md:col-span-2' : ''}>
                              <div className="text-xs font-medium text-gray-600 mb-1">{field.field_name}</div>
                              <div className="text-sm text-gray-900">
                                {field.field_type === 'checkbox' ? (value ? 'Yes' : 'No') : value}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex items-center justify-between mt-8">
                {currentStep > 0 && (
                  <button
                    onClick={handleBack}
                    className="flex items-center gap-2 px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Back
                  </button>
                )}

                {/* Hide Continue button on Step 0 for boarding (Check Availability is present) */}
                {!(reservationType === 'boarding' && currentStep === 0) && (
                  <>
                    {currentStep < steps.length - 1 ? (
                      <button
                        onClick={handleNext}
                        className="ml-auto flex items-center gap-2 px-6 py-2 text-white rounded-lg"
                        style={{ backgroundColor: primaryColor }}
                      >
                        Continue
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    ) : (
                      <button
                        onClick={() => handleBooking(false)}
                        disabled={loading}
                        className="ml-auto flex items-center gap-2 px-6 py-2 text-white rounded-lg disabled:opacity-50"
                        style={{ backgroundColor: primaryColor }}
                      >
                        {loading ? 'Processing...' : 'Reserve Now'}
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Service Detail Modal - BookAppointment Style */}
        {showServiceModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
              {/* Header */}
              <div className="px-6 py-3 flex items-center justify-between border-b" style={{ backgroundColor: primaryColor }}>
                <h3 className="text-lg font-semibold text-white">Service Details</h3>
                <button
                  onClick={() => setShowServiceModal(false)}
                  className="p-1 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-white" />
                </button>
              </div>

              {/* Content - Scrollable */}
              <div className="p-6 overflow-y-auto flex-1">
                {/* Image and Basic Info Section */}
                <div className="flex gap-4 mb-6">
                  {/* Service Image - Left Side */}
                  <div className="w-40 h-40 flex-shrink-0 bg-gray-50 rounded-lg">
                    {showServiceModal.image_url ? (
                      <img 
                        src={showServiceModal.image_url} 
                        alt={showServiceModal.service_name}
                        className="w-full h-full object-contain rounded-lg"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-100 flex items-center justify-center rounded-lg">
                        <Package className="h-16 w-16 text-gray-400" />
                      </div>
                    )}
                  </div>

                  {/* Service Info - Right Side */}
                  <div className="flex-1 min-w-0">
                    <h4 className="text-lg font-bold text-gray-900 mb-2">{showServiceModal.service_name || showServiceModal.name}</h4>
                    <div className="flex items-center gap-4 mb-2">
                      {/* Display duration range for variable services */}
                      {showServiceModal.is_variable && showServiceModal.variants && showServiceModal.variants.length > 0 ? (
                        (() => {
                          const enabledVariants = showServiceModal.variants.filter(v => v.is_enabled);
                          const durations = enabledVariants.map(v => v.variant_name);
                          const minDuration = durations[0];
                          const maxDuration = durations[durations.length - 1];
                          return (
                            <p className="text-sm text-gray-600 flex items-center">
                              <Clock className="h-4 w-4 mr-1" />
                              {minDuration} - {maxDuration}
                            </p>
                          );
                        })()
                      ) : showServiceModal.duration_minutes && !showServiceModal.is_variable ? (
                        <p className="text-sm text-gray-600 flex items-center">
                          <Clock className="h-4 w-4 mr-1" />
                          {Math.floor(showServiceModal.duration_minutes / 60)} hours
                        </p>
                      ) : null}
                      
                      {/* Display price range for variable services */}
                      {showServiceModal.is_variable && showServiceModal.variants && showServiceModal.variants.length > 0 ? (
                        (() => {
                          const enabledVariants = showServiceModal.variants.filter(v => v.is_enabled);
                          const prices = enabledVariants.map(v => v.price);
                          const minPrice = Math.min(...prices);
                          const maxPrice = Math.max(...prices);
                          return (
                            <p className="text-base font-bold text-gray-900 flex items-center">
                              <DollarSign className="h-4 w-4" />
                              {minPrice.toFixed(2)} - ${maxPrice.toFixed(2)}
                            </p>
                          );
                        })()
                      ) : showServiceModal.price ? (
                        <p className="text-base font-bold text-gray-900 flex items-center">
                          <DollarSign className="h-4 w-4" />
                          {showServiceModal.price.toFixed(2)}
                        </p>
                      ) : null}
                    </div>
                    
                    {showServiceModal.description && (
                      <p className="text-sm text-gray-600">{showServiceModal.description}</p>
                    )}
                    
                    {/* Space Info */}
                    {showServiceModal.space_name && (
                      <p className="text-xs text-gray-500 flex items-center gap-1 mt-2">
                        <Home className="h-3 w-3" />
                        {showServiceModal.space_name}
                      </p>
                    )}
                  </div>
                </div>

                {/* Duration Variants Section - Show for variable services */}
                {showServiceModal.is_variable && showServiceModal.variants && showServiceModal.variants.length > 0 && (
                  <div className="mb-6">
                    <h5 className="text-sm font-semibold text-gray-900 mb-3">Duration Pricing Tiers</h5>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {showServiceModal.variants
                        .filter(v => v.is_enabled)
                        .map((variant, idx) => (
                          <div key={idx} className="border border-gray-200 rounded-lg p-3 text-center">
                            <p className="text-xs text-gray-600 mb-1">
                              <Clock className="h-3 w-3 inline mr-1" />
                              {variant.variant_name}
                            </p>
                            <p className="text-sm font-bold text-gray-900">
                              ${variant.price.toFixed(2)}
                            </p>
                          </div>
                        ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-2 italic">
                      * Final price will be calculated based on your stay duration
                    </p>
                  </div>
                )}

                {/* Service Options */}
                {showServiceModal.has_options && showServiceModal.options && showServiceModal.options.length > 0 && (
                  <div className="mb-6">
                    <h5 className="text-sm font-semibold text-gray-900 mb-3">Service Options</h5>
                    <div className="space-y-4">
                      {showServiceModal.options.map((option) => (
                        <div key={option.option_id} className="border border-gray-200 rounded-lg p-4">
                          <label className="block text-sm font-medium text-gray-900 mb-2">
                            {option.option_name} {option.is_required && <span className="text-red-500">*</span>}
                          </label>
                          
                          {(option.option_type === 'single' || option.option_type === 'checkbox') && (
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={selectedOptions[option.option_id] || false}
                                onChange={(e) => {
                                  setSelectedOptions({
                                    ...selectedOptions,
                                    [option.option_id]: e.target.checked
                                  });
                                }}
                                className="mr-2"
                              />
                              <span className="text-sm text-gray-700">
                                Add {option.option_name}
                                {option.price && ` (+$${option.price})`}
                              </span>
                            </label>
                          )}
                          
                          {option.option_type === 'dropdown' && option.choices && option.choices.length > 0 && (
                            <select
                              onChange={(e) => {
                                const choice = option.choices.find(c => c.choice_id === e.target.value);
                                setSelectedOptions({
                                  ...selectedOptions,
                                  [option.option_id]: choice
                                });
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            >
                              <option value="">Select {option.option_name}</option>
                              {option.choices.map((choice) => (
                                <option key={choice.choice_id} value={choice.choice_id}>
                                  {choice.choice_name} {choice.price && `(+$${parseFloat(choice.price).toFixed(2)})`}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer Buttons */}
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => setShowServiceModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setSelectedService(showServiceModal);
                    setShowServiceModal(false);
                  }}
                  className="px-6 py-2 text-white rounded-lg text-sm font-medium"
                  style={{ backgroundColor: primaryColor }}
                >
                  Select Service
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        {showConfirmationModal && confirmedReservation && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[70] p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
              {/* Compact Sticky Header */}
              <div className="px-6 py-3 border-b border-gray-200 flex items-center justify-between flex-shrink-0" style={{ backgroundColor: primaryColor }}>
                <div>
                  <h3 className="text-lg font-semibold text-white">
                    {reservationType === 'tour' ? 'Tour Scheduled' : 'Reservation Confirmed'}
                  </h3>
                  {confirmedReservation.reservation_number && (
                    <p className="text-xs text-white text-opacity-90 mt-0.5">
                      #{confirmedReservation.reservation_number}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => {
                    setShowConfirmationModal(false);
                    window.location.href = '/';
                  }}
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="p-6 overflow-y-auto flex-1">
                {/* Success Message with Reservation Number */}
                <div className="mb-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                    <div>
                      <p className="text-sm text-gray-700 leading-relaxed">
                        Your {reservationType === 'tour' ? 'tour' : 'boarding reservation'} has been successfully booked
                        {(settings?.email_enabled || settings?.sms_enabled) && (
                          <span>
                            . Confirmation has been sent
                            {settings?.email_enabled && settings?.sms_enabled && ' via email and SMS'}
                            {settings?.email_enabled && !settings?.sms_enabled && ' via email'}
                            {!settings?.email_enabled && settings?.sms_enabled && ' via SMS'}
                          </span>
                        )}.
                      </p>
                    </div>
                    {/* Reservation Number */}
                    {confirmedReservation.reservation_number && (
                      <div className="p-3 bg-blue-50 rounded-lg border-2 border-blue-200 text-right">
                        <div className="text-xs font-medium text-blue-700 mb-0.5">Reservation Number</div>
                        <div className="text-xl text-blue-900 font-bold">{confirmedReservation.reservation_number}</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Reservation Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {reservationType === 'boarding' ? (
                    <>
                      {/* Drop-off */}
                      <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border-2 border-blue-200 shadow-sm">
                        <div className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2 flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          Drop-off
                        </div>
                        <div className="text-sm font-semibold text-gray-900">
                          {checkInDate && new Date(checkInDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                        </div>
                        {checkInTime && (
                          <div className="text-sm text-gray-700 mt-1 flex items-center gap-1">
                            <Clock className="h-3.5 w-3.5" />
                            {checkInTime}
                          </div>
                        )}
                      </div>
                      
                      {/* Pick-up */}
                      <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border-2 border-green-200 shadow-sm">
                        <div className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-2 flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          Pick-up
                        </div>
                        <div className="text-sm font-semibold text-gray-900">
                          {checkOutDate && new Date(checkOutDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                        </div>
                        {checkOutTime && (
                          <div className="text-sm text-gray-700 mt-1 flex items-center gap-1">
                            <Clock className="h-3.5 w-3.5" />
                            {checkOutTime}
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border-2 border-blue-200 shadow-sm">
                        <div className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2 flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          Date
                        </div>
                        <div className="text-sm font-semibold text-gray-900">
                          {selectedDate?.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                        </div>
                      </div>
                      
                      <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border-2 border-green-200 shadow-sm">
                        <div className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-2 flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5" />
                          Time
                        </div>
                        <div className="text-sm font-semibold text-gray-900">{selectedTimeSlot}</div>
                      </div>
                    </>
                  )}

                  {/* Service Details */}
                  {selectedService && (
                    <div className="p-4 bg-blue-50 rounded-lg border-2 border-blue-200 md:col-span-2">
                      <div className="text-sm font-semibold text-blue-900 mb-3 flex items-center gap-2">
                        <Package className="h-4 w-4" />
                        Service Details
                      </div>
                      <div className="space-y-3">
                        <div>
                          <div className="text-base text-gray-900 font-bold">{selectedService.service_name || selectedService.name}</div>
                          {confirmedReservation.assigned_space_name && (
                            <div className="text-sm text-blue-700 mt-1 flex items-center gap-1 font-medium">
                              <Home className="h-4 w-4" />
                              Assigned Space: {confirmedReservation.assigned_space_name}
                            </div>
                          )}
                        </div>

                        {selectedService.description && (
                          <div className="text-sm text-gray-700 bg-white p-3 rounded border border-blue-100">
                            {selectedService.description}
                          </div>
                        )}

                      </div>
                    </div>
                  )}

                  {/* Pricing Summary - Confirmation Modal */}
                  {selectedService && (
                    <div className="md:col-span-2">
                      <div className="space-y-2">
                        {/* Duration Display */}
                        {checkInDate && checkInTime && checkOutDate && checkOutTime && (
                          <div className="flex items-center justify-between text-sm text-gray-600 px-3 py-2 bg-gray-50 rounded">
                            <span>Stay Duration</span>
                            <span className="font-semibold">
                              {(() => {
                                const duration = calculateStayDuration();
                                const days = Math.floor(duration / 24);
                                const hours = Math.floor(duration % 24);
                                if (days > 0) {
                                  return `${days} day${days > 1 ? 's' : ''} ${hours} hr${hours !== 1 ? 's' : ''}`;
                                }
                                return `${duration.toFixed(1)} hours`;
                              })()}
                            </span>
                          </div>
                        )}
                        
                        {/* Base Service Price with Breakdown */}
                        <div className="bg-blue-50 rounded px-3 py-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-700">
                              {selectedService.is_variable ? 'Boarding Service (Duration-based)' : 'Boarding Service'}
                            </span>
                            <span className="font-semibold text-gray-900">
                              ${calculateVariantPrice(selectedService).toFixed(2)}
                            </span>
                          </div>
                          
                          {/* Variant Breakdown for variable services */}
                          {selectedService.is_variable && selectedService.variants && selectedService.variants.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-blue-200 space-y-1">
                              {(() => {
                                const durationHours = calculateStayDuration();
                                const variants = selectedService.variants
                                  .filter(v => v.is_enabled)
                                  .map(v => {
                                    const variantName = v.variant_name.toLowerCase();
                                    let hours = 0;
                                    if (variantName.includes('hr')) {
                                      hours = parseFloat(variantName.replace('hr', ''));
                                    } else if (variantName.includes('min')) {
                                      hours = parseFloat(variantName.replace('min', '')) / 60;
                                    }
                                    return { hours, price: v.price, name: v.variant_name };
                                  })
                                  .sort((a, b) => b.hours - a.hours);

                                let remainingHours = durationHours;
                                const breakdown = [];

                                // Calculate breakdown
                                for (const variant of variants) {
                                  if (variant.hours > 0) {
                                    const count = Math.floor(remainingHours / variant.hours);
                                    if (count > 0) {
                                      remainingHours -= count * variant.hours;
                                      breakdown.push({ ...variant, count });
                                    }
                                  }
                                }

                                // Handle remaining hours
                                if (remainingHours > 0 && variants.length > 0) {
                                  const smallestVariant = variants[variants.length - 1];
                                  const additionalCount = Math.ceil(remainingHours / smallestVariant.hours);
                                  const existingEntry = breakdown.find(b => b.name === smallestVariant.name);
                                  if (existingEntry) {
                                    existingEntry.count += additionalCount;
                                  } else {
                                    breakdown.push({ ...smallestVariant, count: additionalCount });
                                  }
                                }

                                return breakdown.map((item, idx) => (
                                  <div key={idx} className="flex items-center justify-between text-xs text-gray-600">
                                    <span className="ml-3">→ {item.count}× {item.name} @ ${item.price.toFixed(2)}</span>
                                    <span className="font-medium">${(item.count * item.price).toFixed(2)}</span>
                                  </div>
                                ));
                              })()}
                            </div>
                          )}
                        </div>
                        
                        {/* Add-on Options */}
                        {selectedOptions && Object.keys(selectedOptions).length > 0 && (
                          <>
                            {Object.entries(selectedOptions).map(([optionId, value]) => {
                              if (!value) return null;
                              const option = selectedService.options?.find(opt => opt.option_id === optionId);
                              if (!option) return null;
                              
                              let optionPrice = 0;
                              let optionLabel = option.option_name;
                              
                              if ((option.option_type === 'single' || option.option_type === 'checkbox') && value === true && option.price) {
                                optionPrice = parseFloat(option.price);
                              }
                              
                              if (option.option_type === 'dropdown' && value) {
                                const choice = option.choices?.find(c => c.choice_id === value.choice_id);
                                if (choice?.price) {
                                  optionPrice = parseFloat(choice.price);
                                  optionLabel = `${option.option_name}: ${choice.choice_name}`;
                                }
                              }
                              
                              if (optionPrice <= 0) return null;
                              
                              return (
                                <div key={optionId} className="flex items-center justify-between text-sm text-gray-600 px-3 py-1">
                                  <span>+ {optionLabel}</span>
                                  <span className="font-semibold">${optionPrice.toFixed(2)}</span>
                                </div>
                              );
                            })}
                          </>
                        )}
                        
                        {/* Total */}
                        <div className="flex items-center justify-between bg-gradient-to-r from-green-50 to-blue-50 p-3 rounded border-2 border-green-200">
                          <span className="text-sm font-bold text-gray-900">Total Cost</span>
                          <div className="text-lg font-bold text-green-700">
                            ${(() => {
                              let total = calculateVariantPrice(selectedService);
                              
                              // Add option prices
                              if (selectedOptions) {
                                Object.entries(selectedOptions).forEach(([optionId, value]) => {
                                  if (!value) return;
                                  const option = selectedService.options?.find(opt => opt.option_id === optionId);
                                  if (!option) return;
                                  
                                  if ((option.option_type === 'single' || option.option_type === 'checkbox') && value === true && option.price) {
                                    total += parseFloat(option.price);
                                  }
                                  
                                  if (option.option_type === 'dropdown' && value) {
                                    const choice = option.choices?.find(c => c.choice_id === value.choice_id);
                                    if (choice?.price) {
                                      total += parseFloat(choice.price);
                                    }
                                  }
                                });
                              }
                              
                              return total.toFixed(2);
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Form Details */}
                  {formData && Object.keys(formData).length > 0 && (
                    <div className="p-4 bg-gray-50 rounded-lg md:col-span-2">
                      <div className="text-sm font-semibold text-gray-900 mb-3">Your Details</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {(() => {
                          const currentFormFields = reservationType === 'tour' ? tourFields : formFields;
                          return currentFormFields.map(field => {
                            const value = formData[field.id];
                            if (!value) return null;
                            return (
                              <div key={field.id}>
                                <div className="text-xs font-medium text-gray-600 mb-1">{field.field_name}</div>
                                <div className="text-sm text-gray-900">{field.field_type === 'checkbox' ? (value ? 'Yes' : 'No') : value}</div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Room Details */}
                  {roomData && Object.keys(roomData).length > 0 && (
                    <div className="p-4 bg-gray-50 rounded-lg md:col-span-2">
                      <div className="text-sm font-semibold text-gray-900 mb-3">Room Details</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {roomFields.map(field => {
                          const value = roomData[field.id];
                          if (!value) return null;
                          return (
                            <div key={field.id}>
                              <div className="text-xs font-medium text-gray-600 mb-1">{field.field_name}</div>
                              <div className="text-sm text-gray-900">{field.field_type === 'checkbox' ? (value ? 'Yes' : 'No') : value}</div>
                              </div>
                            );
                        })}
                      })()}
                      </div>
                    </div>
                  )}

                  {/* Special Instructions */}
                  {specialInstructionsData && Object.keys(specialInstructionsData).length > 0 && (
                    <div className="p-4 bg-gray-50 rounded-lg md:col-span-2">
                      <div className="text-sm font-semibold text-gray-900 mb-3">Special Instructions</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {specialInstructionFields.map(field => {
                          const value = specialInstructionsData[field.id];
                          if (!value) return null;
                          return (
                            <div key={field.id}>
                              <div className="text-xs font-medium text-gray-600 mb-1">{field.field_name}</div>
                              <div className="text-sm text-gray-900">{field.field_type === 'checkbox' ? (value ? 'Yes' : 'No') : value}</div>
                              </div>
                            );
                        })}
                      })()}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-3 border-t border-gray-200 flex justify-between gap-3 flex-shrink-0">
                {/* Order Online Button - Left Side */}
                {settings?.online_ordering_enabled && (
                  <button
                    onClick={() => {
                      window.location.href = '/order-online';
                    }}
                    className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                  >
                    Order Online
                  </button>
                )}

                {/* Close Button - Right Side */}
                <button
                  onClick={() => {
                    setShowConfirmationModal(false);
                    window.location.href = '/';
                  }}
                  className="ml-auto px-4 py-2 text-sm text-white rounded-lg"
                  style={{ backgroundColor: primaryColor }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Payment Modal */}
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          amount={settings?.token_amount || 0}
          token={token}
          primaryColor={primaryColor}
          onSuccess={(paymentIntentId) => {
            setPaymentCompleted(true);
            setShowPaymentModal(false);
            showMessage('success', 'Payment completed successfully!');
            // Automatically proceed with booking after successful payment
            // Pass true to skip payment check since we just completed payment
            setTimeout(() => {
              handleBooking(true);
            }, 500);
          }}
        />
      </div>
    </div>
    </Elements>
  );
};

export default BookBoarding;
